import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Ticket, Loader2, Calendar, X } from "lucide-react";
import { api, formatErr } from "../../lib/api";

/**
 * PromoAdminCard — CRUD UI for newspaper partner promos.
 *
 * Lets the admin paste in a promo (like "Daily Mirror — 2 free NT family
 * passes, valid 14–28 Oct") in about 30 seconds. Once saved, it feeds:
 *   • the homepage hero banner (auto-shows 3 days before valid_from)
 *   • the campsite detail "Free NT passes nearby" callout
 *   • the weekly newsletter injection block
 */

const PARTNER_PRESETS = [
  { key: "nt", display: "National Trust", keywords: ["National Trust"] },
  { key: "rspb", display: "RSPB", keywords: ["RSPB"] },
  { key: "english_heritage", display: "English Heritage", keywords: ["English Heritage"] },
  { key: "kew", display: "Kew Gardens", keywords: ["Royal Botanic Gardens Kew", "Kew"] },
  { key: "wildlife_trusts", display: "The Wildlife Trusts", keywords: ["Wildlife Trust"] },
  { key: "other", display: "Other", keywords: [] },
];

const EMPTY_PROMO = () => ({
  title: "",
  subtitle: "",
  partner: "nt",
  partner_display: "National Trust",
  partner_keywords: ["National Trust"],
  retailers: [{ name: "", logo_domain: "", purchase_hint: "" }],
  entitlement: "",
  valid_from: "",
  valid_to: "",
  preview_from: "",
  redemption_url: "",
  notes: "",
  enabled: true,
});

export default function PromoAdminCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/promos");
      setItems(data.promos || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing(EMPTY_PROMO());
  const startEdit = (p) => setEditing({ ...p });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing.title || !editing.valid_from || !editing.valid_to || !editing.entitlement) {
      toast.error("Title, entitlement, valid_from, valid_to are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...editing };
      // strip empty retailers
      payload.retailers = (payload.retailers || []).filter((r) => (r.name || "").trim());
      if (payload.promo_id) {
        await api.patch(`/admin/promos/${payload.promo_id}`, payload);
        toast.success("Promo updated");
      } else {
        await api.post("/admin/promos", payload);
        toast.success("Promo created");
      }
      setEditing(null);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const remove = async (promo_id) => {
    if (!window.confirm("Delete this promo?")) return;
    try {
      await api.delete(`/admin/promos/${promo_id}`);
      toast.success("Promo deleted");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const setPartner = (key) => {
    const p = PARTNER_PRESETS.find((x) => x.key === key);
    if (!p) return;
    setEditing((prev) => ({
      ...prev,
      partner: p.key,
      partner_display: prev.partner_display && prev.partner !== p.key ? prev.partner_display : p.display,
      partner_keywords: prev.partner_keywords?.length && prev.partner !== p.key ? prev.partner_keywords : p.keywords,
    }));
  };

  return (
    <section className="border hairline rounded-2xl bg-white p-5 mb-6" data-testid="admin-promos-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="eyebrow text-[#0a7f3f] flex items-center gap-1.5 mb-1">
            <Ticket className="w-3 h-3" /> Newspaper Promos
          </div>
          <h2 className="font-display text-xl">Free-pass giveaways (NT / RSPB / EH…)</h2>
        </div>
        {!editing && (
          <button
            type="button"
            onClick={startNew}
            className="inline-flex items-center gap-1.5 bg-[#0a7f3f] text-white text-xs uppercase tracking-widest font-black px-3 py-2"
            data-testid="promo-new-btn"
          >
            <Plus className="w-3.5 h-3.5" /> New promo
          </button>
        )}
      </div>

      {loading && (
        <div className="text-sm text-[#666] flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading promos…
        </div>
      )}

      {editing && <PromoForm editing={editing} setEditing={setEditing} setPartner={setPartner}
        onSave={save} onCancel={cancel} saving={saving} />}

      {!loading && !editing && (
        <ul className="divide-y hairline" data-testid="promo-list">
          {items.length === 0 && (
            <li className="text-sm text-[#666] italic py-3">No promos yet. Click "New promo" when Reach plc / a newspaper announces the next NT giveaway.</li>
          )}
          {items.map((p) => (
            <li key={p.promo_id} className="py-3 flex items-start gap-3" data-testid={`promo-row-${p.promo_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display text-base">{p.title}</div>
                  {p.is_active_now && <span className="text-[9px] mono uppercase tracking-widest bg-[#0a7f3f] text-white px-1.5 py-0.5">LIVE</span>}
                  {!p.is_active_now && p.is_visible_now && <span className="text-[9px] mono uppercase tracking-widest bg-[#ff4500] text-white px-1.5 py-0.5">TEASER</span>}
                  {!p.enabled && <span className="text-[9px] mono uppercase tracking-widest bg-[#999] text-white px-1.5 py-0.5">DISABLED</span>}
                </div>
                <div className="text-[11px] mono text-[#666] mt-0.5 flex items-center gap-1.5 flex-wrap">
                  <Calendar className="w-3 h-3" /> {p.valid_from} → {p.valid_to} · {p.partner_display}
                </div>
                {p.retailers?.length > 0 && (
                  <div className="text-[11px] text-[#666] mt-1">Papers: {p.retailers.map((r) => r.name).join(", ")}</div>
                )}
              </div>
              <button type="button" onClick={() => startEdit(p)} className="text-xs text-[#0a7f3f] hover:underline p-1" data-testid={`promo-edit-${p.promo_id}`}>
                <Edit3 className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={() => remove(p.promo_id)} className="text-xs text-[#b00020] hover:underline p-1" data-testid={`promo-delete-${p.promo_id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function PromoForm({ editing, setEditing, setPartner, onSave, onCancel, saving }) {
  const set = (k, v) => setEditing((prev) => ({ ...prev, [k]: v }));
  const setRet = (i, k, v) => setEditing((prev) => {
    const rs = [...(prev.retailers || [])];
    rs[i] = { ...rs[i], [k]: v };
    return { ...prev, retailers: rs };
  });
  const addRet = () => setEditing((prev) => ({
    ...prev, retailers: [...(prev.retailers || []), { name: "", logo_domain: "", purchase_hint: "" }],
  }));
  const rmRet = (i) => setEditing((prev) => ({
    ...prev, retailers: (prev.retailers || []).filter((_, idx) => idx !== i),
  }));

  const Input = ({ label, k, ...rest }) => (
    <label className="block">
      <span className="block text-[10px] mono uppercase tracking-widest text-[#666] mb-1">{label}</span>
      <input
        {...rest}
        value={editing[k] || ""}
        onChange={(e) => set(k, e.target.value)}
        className="w-full border hairline px-2.5 py-1.5 text-sm"
      />
    </label>
  );

  return (
    <div className="border hairline bg-[#fafafa] p-4 space-y-3" data-testid="promo-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <Input label="Title *" k="title" placeholder="2 Free National Trust Family Passes" data-testid="promo-title" />
        <div>
          <span className="block text-[10px] mono uppercase tracking-widest text-[#666] mb-1">Partner *</span>
          <select
            value={editing.partner || "nt"}
            onChange={(e) => setPartner(e.target.value)}
            className="w-full border hairline px-2.5 py-1.5 text-sm"
            data-testid="promo-partner"
          >
            {PARTNER_PRESETS.map((p) => (
              <option key={p.key} value={p.key}>{p.display}</option>
            ))}
          </select>
        </div>
      </div>

      <Input label="Subtitle" k="subtitle" placeholder="Family days out — grab yours before they run out" data-testid="promo-subtitle" />
      <Input label="Entitlement *" k="entitlement" placeholder="1 adult + 4 children (or 2 adults + 3 kids), single visit" data-testid="promo-entitlement" />

      <div className="grid sm:grid-cols-3 gap-3">
        <Input label="Valid from *" k="valid_from" type="date" data-testid="promo-valid-from" />
        <Input label="Valid to *" k="valid_to" type="date" data-testid="promo-valid-to" />
        <Input label="Preview from" k="preview_from" type="date" data-testid="promo-preview-from" />
      </div>

      <Input label="Redemption URL" k="redemption_url" placeholder="https://www.inyourarea.co.uk/..." data-testid="promo-redemption-url" />

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="block text-[10px] mono uppercase tracking-widest text-[#666]">Participating newspapers</span>
          <button type="button" onClick={addRet} className="text-[11px] text-[#0a7f3f] hover:underline" data-testid="promo-add-retailer">
            + Add newspaper
          </button>
        </div>
        <div className="space-y-2">
          {(editing.retailers || []).map((r, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center bg-white border hairline p-2">
              <input value={r.name} onChange={(e) => setRet(i, "name", e.target.value)}
                placeholder="Daily Mirror" className="border hairline px-2 py-1 text-sm" data-testid={`promo-retailer-name-${i}`} />
              <input value={r.logo_domain} onChange={(e) => setRet(i, "logo_domain", e.target.value)}
                placeholder="mirror.co.uk" className="border hairline px-2 py-1 text-sm" data-testid={`promo-retailer-domain-${i}`} />
              <input value={r.purchase_hint} onChange={(e) => setRet(i, "purchase_hint", e.target.value)}
                placeholder="Sat/Sun edition" className="border hairline px-2 py-1 text-sm" data-testid={`promo-retailer-hint-${i}`} />
              <button type="button" onClick={() => rmRet(i)} className="text-[#b00020] p-1" data-testid={`promo-retailer-rm-${i}`}>
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <label className="inline-flex items-center gap-2 text-sm">
        <input type="checkbox" checked={editing.enabled !== false}
          onChange={(e) => set("enabled", e.target.checked)} data-testid="promo-enabled" />
        Enabled (shown to users)
      </label>

      <div className="flex items-center gap-2 pt-2">
        <button type="button" onClick={onSave} disabled={saving}
          className="bg-[#0a7f3f] text-white font-black text-xs uppercase tracking-widest px-4 py-2 disabled:opacity-60"
          data-testid="promo-save">
          {saving ? "Saving…" : (editing.promo_id ? "Update promo" : "Create promo")}
        </button>
        <button type="button" onClick={onCancel}
          className="border hairline font-black text-xs uppercase tracking-widest px-4 py-2 hover:bg-[#f6f6f6]"
          data-testid="promo-cancel">
          Cancel
        </button>
      </div>
    </div>
  );
}
